import { useState, useEffect, useCallback } from 'react';
import { cn } from '../lib/utils';
import { api } from '../core/apiClient';
import { Numpad } from '../components/shop-floor/Numpad';
import { useToast } from '../components/ui/toast';
import {
  Clock,
  LogIn,
  LogOut,
  Play,
  Search,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Wrench,
} from 'lucide-react';

// Types
interface EmployeeStatus {
  isClockedIn: boolean;
  clockInTime?: string;
  workedHours?: number;
  activeWorkOrders: Array<{
    workOrderId: string;
    orderNumber: string;
    hoursToday: number;
  }>;
}

interface WorkOrder {
  id: string;
  orderNumber: string;
  name: string;
  projectNumber?: string;
  projectName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface WorkLog {
  workOrderId: string;
  orderNumber?: string;
  hours: number;
  description?: string;
}

interface DailySummary {
  date: string;
  attendanceHours: number;
  imputedHours: number;
  pendingHours: number;
  isComplete: boolean;
  workLogs: WorkLog[];
}

// Mock employee ID (in production this comes from auth)
const MOCK_EMPLOYEE_ID = 'emp-001';

/**
 * Terminal de Taller - Interfície Mobile-First per a Operaris
 * Dissenyat per a operaris amb pantalla tactil
 */
export default function ShopFloor() {
  const { showToast } = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<EmployeeStatus | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [dailySummary, setDailySummary] = useState<DailySummary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOT, setSelectedOT] = useState<WorkOrder | null>(null);
  const [showNumpad, setShowNumpad] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, workOrdersRes, summaryRes] = await Promise.all([
        api.get<{ success: boolean; data: EmployeeStatus }>(`/shop-floor/status/${MOCK_EMPLOYEE_ID}`),
        api.get<{ success: boolean; data: WorkOrder[] }>('/shop-floor/available-work-orders'),
        api.get<{ success: boolean; data: DailySummary }>(`/shop-floor/daily-summary/${MOCK_EMPLOYEE_ID}`),
      ]);
      setStatus(statusRes.data);
      setWorkOrders(workOrdersRes.data);
      setDailySummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Mock data for development
      setStatus({
        isClockedIn: false,
        activeWorkOrders: [],
      });
      setWorkOrders([
        { id: '1', orderNumber: 'OT-2026-00123', name: 'Rampa hidraulica 12m', projectNumber: 'EQ-2026-00045', projectName: 'Hotel Meridien', priority: 'high' },
        { id: '2', orderNumber: 'OT-2026-00124', name: 'Plataforma elevadora', projectNumber: 'SR-2026-00012', projectName: 'Centre Comercial', priority: 'medium' },
        { id: '3', orderNumber: 'OT-2026-00125', name: 'Manteniment preventiu', projectNumber: 'EQ-2026-00046', projectName: 'Parking Municipal', priority: 'low' },
        { id: '4', orderNumber: 'OT-2026-00126', name: 'Reparació motor', projectNumber: 'SR-2026-00013', projectName: 'Nau Industrial', priority: 'urgent' },
      ]);
      setDailySummary({
        date: new Date().toISOString().split('T')[0],
        attendanceHours: 0,
        imputedHours: 0,
        pendingHours: 0,
        isComplete: false,
        workLogs: [],
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Clock In
  const handleClockIn = async () => {
    try {
      setActionLoading(true);
      await api.post('/shop-floor/clock-in', {
        employeeId: MOCK_EMPLOYEE_ID,
        locationName: 'Terminal Taller',
      });
      showToast('Entrada registrada correctament', 'success');
      await loadData();
    } catch (error) {
      console.error('Error clocking in:', error);
      // Mock success for development
      setStatus((prev) => prev ? { ...prev, isClockedIn: true, clockInTime: new Date().toISOString() } : null);
      showToast('Entrada registrada correctament', 'success');
    } finally {
      setActionLoading(false);
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    try {
      setActionLoading(true);
      await api.post('/shop-floor/clock-out', {
        employeeId: MOCK_EMPLOYEE_ID,
        locationName: 'Terminal Taller',
      });
      showToast('Sortida registrada correctament', 'success');
      await loadData();
    } catch (error) {
      console.error('Error clocking out:', error);
      // Mock success for development
      setStatus((prev) => prev ? { ...prev, isClockedIn: false } : null);
      showToast('Sortida registrada correctament', 'success');
    } finally {
      setActionLoading(false);
    }
  };

  // Log Time
  const handleLogTime = async (hours: number) => {
    if (!selectedOT) return;

    try {
      setActionLoading(true);
      await api.post('/shop-floor/log-time', {
        employeeId: MOCK_EMPLOYEE_ID,
        workOrderId: selectedOT.id,
        hours,
        workType: 'production',
      });
      showToast(`${hours}h imputades a ${selectedOT.orderNumber}`, 'success');
      setShowNumpad(false);
      setSelectedOT(null);
      await loadData();
    } catch (error) {
      console.error('Error logging time:', error);
      // Mock success for development
      setDailySummary((prev) => prev ? {
        ...prev,
        imputedHours: prev.imputedHours + hours,
        pendingHours: Math.max(0, prev.pendingHours - hours),
        workLogs: [...prev.workLogs, { workOrderId: selectedOT.id, orderNumber: selectedOT.orderNumber, hours }],
      } : null);
      showToast(`${hours}h imputades a ${selectedOT.orderNumber}`, 'success');
      setShowNumpad(false);
      setSelectedOT(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter work orders
  const filteredWorkOrders = workOrders.filter((wo) =>
    wo.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.projectNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wo.projectName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate worked time
  const getWorkedTime = () => {
    if (!status?.clockInTime) return null;
    const start = new Date(status.clockInTime);
    const diff = Math.floor((currentTime.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return { hours, minutes };
  };

  const workedTime = getWorkedTime();

  // Priority colors
  const priorityColors = {
    low: 'bg-slate-600',
    medium: 'bg-blue-600',
    high: 'bg-amber-600',
    urgent: 'bg-red-600',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  // Show Numpad overlay when selecting OT
  if (showNumpad && selectedOT) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 flex flex-col">
        <div className="text-center mb-6">
          <p className="text-slate-400 text-sm">Imputant a</p>
          <h2 className="text-sm font-bold text-white">{selectedOT.orderNumber}</h2>
          <p className="text-slate-500 text-sm">{selectedOT.name}</p>
        </div>
        <Numpad
          onConfirm={handleLogTime}
          onCancel={() => {
            setShowNumpad(false);
            setSelectedOT(null);
          }}
          label="Hores treballades"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header with Clock */}
      <header className="bg-slate-900 border-b border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Wrench className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-bold text-sm">Terminal Taller</h1>
              <p className="text-slate-400 text-xs">Roig i Fills</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold tabular-nums">
              {currentTime.toLocaleTimeString('ca-ES', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-slate-400 text-xs">
              {currentTime.toLocaleDateString('ca-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 space-y-4">
        {/* Attendance Widget */}
        <section className="bg-slate-900 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-slate-400" />
            <h2 className="font-semibold text-slate-300">Control de Jornada</h2>
          </div>

          {status?.isClockedIn ? (
            <>
              {/* Clocked in state */}
              <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 bg-emerald-500 rounded-full animate-pulse" />
                  <div>
                    <p className="text-emerald-400 font-medium">Treballant</p>
                    {workedTime && (
                      <p className="text-2xl font-bold text-white">
                        {workedTime.hours}h {workedTime.minutes}m
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleClockOut}
                disabled={actionLoading}
                className={cn(
                  'w-full h-16 rounded-xl text-sm font-bold transition-colors',
                  'bg-red-600 hover:bg-red-500 active:bg-red-400',
                  'flex items-center justify-center gap-3',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <LogOut className="h-6 w-6" />
                    FITXAR SORTIDA
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Clocked out state */}
              <div className="bg-slate-800 rounded-xl p-4 mb-4">
                <p className="text-slate-400 text-center">No has fitxat entrada avui</p>
              </div>
              <button
                onClick={handleClockIn}
                disabled={actionLoading}
                className={cn(
                  'w-full h-16 rounded-xl text-sm font-bold transition-colors',
                  'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-400',
                  'flex items-center justify-center gap-3',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <LogIn className="h-6 w-6" />
                    FITXAR ENTRADA
                  </>
                )}
              </button>
            </>
          )}
        </section>

        {/* Work Order Selector - Only show if clocked in */}
        {status?.isClockedIn && (
          <section className="bg-slate-900 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Play className="h-5 w-5 text-slate-400" />
                <h2 className="font-semibold text-slate-300">Ordres de Treball</h2>
              </div>
              <span className="text-xs text-slate-500">{filteredWorkOrders.length} disponibles</span>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cercar OT, projecte o client..."
                className={cn(
                  'w-full h-12 pl-10 pr-4 rounded-xl text-white placeholder:text-slate-500',
                  'bg-slate-800 border border-slate-700 focus:border-blue-500 focus:outline-none'
                )}
              />
            </div>

            {/* Work Order List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredWorkOrders.map((wo) => (
                <button
                  key={wo.id}
                  onClick={() => {
                    setSelectedOT(wo);
                    setShowNumpad(true);
                  }}
                  className={cn(
                    'w-full p-4 rounded-xl text-left transition-colors',
                    'bg-slate-800 hover:bg-slate-700 active:bg-slate-600',
                    'flex items-center justify-between gap-3'
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn('h-3 w-3 rounded-full flex-shrink-0', priorityColors[wo.priority])} />
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">{wo.orderNumber}</p>
                      <p className="text-sm text-slate-400 truncate">{wo.name}</p>
                      <p className="text-xs text-slate-500 truncate">{wo.projectNumber} - {wo.projectName}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Daily Summary */}
        {dailySummary && (
          <section className="bg-slate-900 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-300">Resum del Dia</h2>
              {dailySummary.isComplete ? (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Complet
                </span>
              ) : dailySummary.pendingHours > 0 ? (
                <span className="flex items-center gap-1 text-xs text-amber-400">
                  <AlertTriangle className="h-4 w-4" />
                  {dailySummary.pendingHours.toFixed(1)}h pendents
                </span>
              ) : null}
            </div>

            {/* Hours Progress */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-800 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-white">{dailySummary.attendanceHours.toFixed(1)}h</p>
                <p className="text-xs text-slate-500">Fitxades</p>
              </div>
              <div className="bg-slate-800 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-blue-400">{dailySummary.imputedHours.toFixed(1)}h</p>
                <p className="text-xs text-slate-500">Imputades</p>
              </div>
            </div>

            {/* Work Logs */}
            {dailySummary.workLogs.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Tasques d&apos;avui</p>
                {dailySummary.workLogs.map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-slate-800 rounded-lg p-3">
                    <span className="text-sm text-slate-300">{log.orderNumber}</span>
                    <span className="text-sm font-medium text-white">{log.hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
